import React, { useState } from 'react';
import { Map, Edit3, Upload, Lock, Folder } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import LoginRequiredModal from './LoginRequiredModal';

const roadmapModes = [
  { 
    key: 'create', 
    label: 'Criar via interface', 
    description: 'Construa seu roadmap diretamente no navegador',
    icon: Edit3,
    color: 'green',
    requiresAuth: true
  },
  { 
    key: 'upload', 
    label: 'Upload de arquivo', 
    description: 'Importe dados de um arquivo CSV ou XLSX',
    icon: Upload,
    color: 'blue',
    requiresAuth: false
  },
  { 
    key: 'saved', 
    label: 'Roadmaps salvos', 
    description: 'Acesse e gerencie seus roadmaps salvos',
    icon: Folder,
    color: 'purple',
    requiresAuth: true
  }
];

function RoadmapModeSelector({ selected, onSelect, onBack }) {
  const { isAuthenticated, login, register } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleModeSelect = (mode) => {
    // Se o modo requer autenticação e usuário não está logado
    if (mode.requiresAuth && !isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    // Proceder normalmente
    onSelect(mode.key);
  };

  const handleLogin = () => {
    // O modal de login já está sendo tratado dentro do LoginRequiredModal
    // Não precisamos fechar aqui, o próprio modal se encarrega
  };

  const handleRegister = () => {
    // O modal de registro já está sendo tratado dentro do LoginRequiredModal  
    // Não precisamos fechar aqui, o próprio modal se encarrega
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8">
        {/* Title Section */}
        <div className="text-center mb-6">
          {/* Back Button - Alinhado ao título */}
          <div className="flex justify-center mb-4">
            <button
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:text-blue-600 hover:border-blue-300 font-medium transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={onBack}
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Voltar
            </button>
          </div>
          
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full mb-4">
            <Map className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800 font-medium text-sm">Roadmap</span>
          </div>
          
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Como você quer criar o roadmap?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            Escolha entre criar interativamente ou importar dados existentes
          </p>
        </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
        {roadmapModes.map(mode => {
          const isSelected = selected === mode.key;
          const needsAuth = mode.requiresAuth && !isAuthenticated;
          
          return (
            <button
              key={mode.key}
              onClick={() => handleModeSelect(mode)}
              className={`group relative bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border text-left ${
                isSelected 
                  ? 'border-blue-500 ring-2 ring-blue-200' 
                  : 'border-gray-200 hover:border-blue-300'
              } ${needsAuth ? 'opacity-90' : ''}`}
            >
              {/* Auth Required Indicator */}
              {needsAuth && (
                <div className="absolute top-3 right-3 text-amber-500">
                  <Lock className="w-5 h-5" />
                </div>
              )}
              
              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              )}
              
              {/* Icon */}
              <div className={`w-14 h-14 rounded-2xl ${
                mode.color === 'green' 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50' 
                  : mode.color === 'blue'
                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50'
                    : 'bg-gradient-to-r from-purple-50 to-violet-50'
              } flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <div className={`w-10 h-10 rounded-xl ${
                  mode.color === 'green' 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                    : mode.color === 'blue'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
                      : 'bg-gradient-to-r from-purple-500 to-violet-500'
                } flex items-center justify-center`}>
                  <mode.icon className="w-5 h-5 text-white" />
                </div>
              </div>
              
              {/* Content */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {mode.label}
                  {needsAuth && (
                    <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                      Login necessário
                    </span>
                  )}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {mode.description}
                  {needsAuth && (
                    <span className="block mt-1 text-amber-600 text-xs">
                      🔒 Requer conta para recursos avançados
                    </span>
                  )}
                </p>
              </div>
              
              {/* Hover Arrow */}
              <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white transform rotate-180" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Login Required Modal */}
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
        onRegister={handleRegister}
        feature="interface"
      />
      </div>
    </div>
  );
}

export default RoadmapModeSelector;
